import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeHex = (value) => {
  if (typeof value !== 'string') return null;
  let hex = value.trim().replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (!/^([0-9a-fA-F]{6})$/.test(hex)) {
    return null;
  }
  return `#${hex.toLowerCase()}`;
};

const hexToHsl = (value) => {
  const normalized = normalizeHex(value);
  if (!normalized) {
    return '222.2 84% 4.9%';
  }

  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  const hue = Math.round(h * 10) / 10;
  const saturation = Math.round(clamp(s * 100, 0, 100) * 10) / 10;
  const lightness = Math.round(clamp(l * 100, 0, 100) * 10) / 10;

  return `${hue} ${saturation}% ${lightness}%`;
};

export const DEFAULT_DARK_BACKGROUND = '#0f172a';
const DEFAULT_DARK_BACKGROUND_HSL = hexToHsl(DEFAULT_DARK_BACKGROUND);

/**
 * Theme store using Zustand for dark mode management
 * Persists theme preference to localStorage
 */
export const useThemeStore = create(
  persist(
    (set, get) => {
      const applyDarkBackground = (hex, hsl) => {
        const safeHex = normalizeHex(hex) || DEFAULT_DARK_BACKGROUND;
        const safeHsl = hsl || hexToHsl(safeHex);
        document.documentElement.style.setProperty('--sm-dark-background-hex', safeHex);
        document.documentElement.style.setProperty('--sm-dark-background-hsl', safeHsl);
      };

      return {
      // State
      isDarkMode: false,
      darkBackground: DEFAULT_DARK_BACKGROUND,
      darkBackgroundHsl: DEFAULT_DARK_BACKGROUND_HSL,
      
      // Actions
      toggleDarkMode: () => {
        set((state) => {
          const newDarkMode = !state.isDarkMode;
          // Apply to document root immediately
          if (newDarkMode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
          return { isDarkMode: newDarkMode };
        });
      },
      
      setDarkMode: (isDark) => {
        set({ isDarkMode: isDark });
        // Apply to document root immediately
        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      setDarkBackground: (value) => {
        const normalized = normalizeHex(value) || DEFAULT_DARK_BACKGROUND;
        const hsl = hexToHsl(normalized);
        applyDarkBackground(normalized, hsl);
        set({ darkBackground: normalized, darkBackgroundHsl: hsl });
      },

      resetDarkBackground: () => {
        applyDarkBackground(DEFAULT_DARK_BACKGROUND, DEFAULT_DARK_BACKGROUND_HSL);
        set({
          darkBackground: DEFAULT_DARK_BACKGROUND,
          darkBackgroundHsl: DEFAULT_DARK_BACKGROUND_HSL,
        });
      },
      
      // Initialize theme on load
      initTheme: () => {
        const stored = localStorage.getItem('theme-storage');
        let isDark = get().isDarkMode;
        let backgroundHex = get().darkBackground;
        let backgroundHsl = get().darkBackgroundHsl;

        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const state = parsed.state || {};
            const persistedDark = typeof state.isDarkMode === 'boolean' ? state.isDarkMode : null;
            const persistedBackground = normalizeHex(state.darkBackground);
            const persistedHsl = typeof state.darkBackgroundHsl === 'string' ? state.darkBackgroundHsl : null;

            if (persistedDark !== null) {
              isDark = persistedDark;
            }

            if (persistedBackground) {
              backgroundHex = persistedBackground;
              backgroundHsl = persistedHsl || hexToHsl(persistedBackground);
            }
          } catch (e) {
            // If parsing fails, use system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            isDark = prefersDark;
          }
        } else {
          // No stored preference, check system preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          isDark = prefersDark;
        }

        applyDarkBackground(backgroundHex, backgroundHsl);

        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        set({
          isDarkMode: isDark,
          darkBackground: backgroundHex,
          darkBackgroundHsl: backgroundHsl,
        });
      },
    };
    },
    {
      name: 'theme-storage',
      partialize: (state) => ({
        isDarkMode: state.isDarkMode,
        darkBackground: state.darkBackground,
        darkBackgroundHsl: state.darkBackgroundHsl,
      }),
    }
  )
);

